import React, { useEffect, useState } from 'react';
import axios from "axios";
import { Box, Typography, Paper, Grid } from '@mui/material';
import { styled } from '@mui/system';
import { Bar } from 'react-chartjs-2';
import electricity from './images/electricity.png';
import shower from './images/shower.png';
import solarpanel from './images/solarpanel.png';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const DashboardCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    height: '100%',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
}));

const CardHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
});

const CardIcon = styled('img')({
    width: '60px',
    height: '60px',
    marginRight: '8px',
});

const StatRow = styled(Box)({
    marginBottom: '12px',
});

const EfficiencyDashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [solarData, setSolarData] = useState([]);
    const [weatherData, setWeatherData] = useState([]);

    // Summary values
    const [energyLastMonth, setEnergyLastMonth] = useState(null);
    const [energyLastYearSameMonth, setEnergyLastYearSameMonth] = useState(null);
    const [waterLastMonth, setWaterLastMonth] = useState(null);
    const [waterLastYearSameMonth, setWaterLastYearSameMonth] = useState(null);

    // Monthly chart data (last 12 months)
    const [energyMonthlyData, setEnergyMonthlyData] = useState({
        labels: [],
        datasets: [{ label: 'Monthly Average (kWh/flat)', data: [], backgroundColor: 'rgba(75, 192, 192, 0.6)' }]
    });
    const [waterMonthlyData, setWaterMonthlyData] = useState({
        labels: [],
        datasets: [{ label: 'Total Water (m³/Estate)', data: [], backgroundColor: 'rgba(54, 162, 235, 0.6)' }]
    });

    const getComparisonMonths = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        let lastMonthYear = currentYear;
        let lastMonth = currentMonth - 1;
        if (lastMonth === 0) {
            lastMonth = 12;
            lastMonthYear--;
        }
        let lastYearMonth = currentMonth;
        let lastYearYear = currentYear - 1;
        return {
            lastMonth: { year: lastMonthYear, month: lastMonth },
            lastYearSame: { year: lastYearYear, month: lastYearMonth }
        };
    };

    // Fetch monthly (last 12 months)
    const fetchMonthlyData = async () => {
        try {
            const energyRes = await axios.get('https://futipiot.ddns.net/api/meters/energy-monthly?months=12');
            const monthlyEnergy = energyRes.data;
            setEnergyMonthlyData({
                labels: monthlyEnergy.map(m => m.monthName),
                datasets: [{
                    label: 'Monthly Average (kWh/flat)',
                    data: monthlyEnergy.map(m => m.averageKwhPerFlat),
                    backgroundColor: 'rgba(75, 192, 192, 0.6)',
                }]
            });

            const waterRes = await axios.get('https://futipiot.ddns.net/api/meters/water-monthly?months=12');
            const monthlyWater = waterRes.data;
            setWaterMonthlyData({
                labels: monthlyWater.map(m => m.monthName),
                datasets: [{
                    label: 'Total Water (m³/Estate)',
                    data: monthlyWater.map(m => m.totalM3),
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                }]
            });
        } catch (error) {
            console.error('Error fetching monthly data:', error);
        }
    };

    const fetchConsumptionData = async () => {
        const { lastMonth, lastYearSame } = getComparisonMonths();
        try {
            const energyLastMonthRes = await axios.get(`https://futipiot.ddns.net/api/meters/energy-consumption?year=${lastMonth.year}&month=${lastMonth.month}`);
            const energyLastYearRes = await axios.get(`https://futipiot.ddns.net/api/meters/energy-consumption?year=${lastYearSame.year}&month=${lastYearSame.month}`);
            setEnergyLastMonth(energyLastMonthRes.data.averagePerFlatKwh);
            setEnergyLastYearSameMonth(energyLastYearRes.data.averagePerFlatKwh);

            const waterLastMonthRes = await axios.get(`https://futipiot.ddns.net/api/meters/water-consumption?year=${lastMonth.year}&month=${lastMonth.month}`);
            const waterLastYearRes = await axios.get(`https://futipiot.ddns.net/api/meters/water-consumption?year=${lastYearSame.year}&month=${lastYearSame.month}`);
            setWaterLastMonth(waterLastMonthRes.data.totalConsumptionM3);
            setWaterLastYearSameMonth(waterLastYearRes.data.totalConsumptionM3);
        } catch (error) {
            console.error('Error fetching consumption data:', error);
        }
    };

    useEffect(() => {
        const httpClient = axios.create({
            baseURL: 'https://futipiot.ddns.net',
            headers: { 'Content-Type': 'application/json' }
        });

        const getSolarData = async () => {
            try {
                const response = await httpClient.get('/api/meters/solar_panel_data');
                setSolarData(response.data || []);
            } catch (error) {
                console.error('Error fetching solar data:', error);
                setSolarData([]);
            }
        };

        const getWeatherData = async () => {
            try {
                const response = await httpClient.get('/api/meters/weather_station_data');
                setWeatherData(response.data || []);
            } catch (error) {
                console.error('Error fetching weather data:', error);
                setWeatherData([]);
            }
        };

        const fetchAll = async () => {
            setIsLoading(true);
            await Promise.all([getSolarData(), getWeatherData(), fetchConsumptionData(), fetchMonthlyData()]);
            setIsLoading(false);
        };

        fetchAll();
        const interval = setInterval(fetchAll, 30000);
        return () => clearInterval(interval);
    }, []);

    const latestSolarData = solarData.length > 0 ? solarData[0] : null;
    const latestWeatherData = weatherData.length > 0 ? weatherData[0] : null;

    return (
        <Box sx={{ flexGrow: 1, padding: 3 }}>
            {isLoading ? (
                <Typography variant="h6" align="center">Loading...</Typography>
            ) : (
                <Grid container spacing={3}>
                    {/* Row 1: Four summary cards */}
                    <Grid item xs={12} md={3}>
                        <DashboardCard>
                            <CardHeader>
                                <CardIcon src={electricity} alt="House" />
                                <Typography variant="h6">Tenants Energy Consumption</Typography>
                            </CardHeader>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Last Month Average</Typography>
                                <Typography variant="h5">{energyLastMonth !== null ? `${energyLastMonth.toFixed(2)} kWh` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Last Year Same Month</Typography>
                                <Typography variant="h5">{energyLastYearSameMonth !== null ? `${energyLastYearSameMonth.toFixed(2)} kWh` : 'No data'}</Typography>
                            </StatRow>
                            <Box sx={{ flexGrow: 1, marginTop: 'auto' }}>
                                <Typography variant="body2" color="textSecondary">Monthly Average (Last 12 Months)</Typography>
                                <Box sx={{ height: '200px' }}>
                                    <Bar data={energyMonthlyData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                                </Box>
                            </Box>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <DashboardCard>
                            <CardHeader>
                                <CardIcon src={shower} alt="Shower" />
                                <Typography variant="h6">Total Water Consumption</Typography>
                            </CardHeader>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Last Month Average</Typography>
                                <Typography variant="h5">{waterLastMonth !== null ? `${waterLastMonth.toFixed(2)} m³` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Last Year Same Month</Typography>
                                <Typography variant="h5">{waterLastYearSameMonth !== null ? `${waterLastYearSameMonth.toFixed(2)} m³` : 'No data'}</Typography>
                            </StatRow>
                            <Box sx={{ flexGrow: 1, marginTop: 'auto' }}>
                                <Typography variant="body2" color="textSecondary">Monthly Average (Last 12 Months)</Typography>
                                <Box sx={{ height: '200px' }}>
                                    <Bar data={waterMonthlyData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
                                </Box>
                            </Box>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <DashboardCard>
                            <CardHeader>
                                <CardIcon src={solarpanel} alt="Solar Panel" />
                                <Typography variant="h6">Solar Panel Output</Typography>
                            </CardHeader>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Total Energy Generation</Typography>
                                <Typography variant="h5">{latestSolarData ? `${(latestSolarData.totalEnergyGeneration / 1000).toFixed(2)} kWh` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                                <Typography variant="body1">{latestSolarData ? new Date(latestSolarData.timestamp).toLocaleString() : 'No data'}</Typography>
                            </StatRow>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <DashboardCard>
                            <CardHeader>
                                <Typography variant="h6">Environment</Typography>
                            </CardHeader>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Outdoor Temperature</Typography>
                                    <Typography variant="h5">{latestWeatherData ? `${(latestWeatherData.pvPanelTemperature / 10).toFixed(1) } °C` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Outside Humidity</Typography>
                                <Typography variant="h5">{latestWeatherData ? `${latestWeatherData.outsideHumidity.toFixed(1)} %` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Wind Speed</Typography>
                                    <Typography variant="h5">{latestWeatherData ? `${latestWeatherData.windspeed.toFixed(1) } m/s` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Solar Radiation</Typography>
                                    <Typography variant="h5">{latestWeatherData ? `${latestWeatherData.solarRadiation.toFixed(1) } W/m²` : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">UV Index</Typography>
                                    <Typography variant="h5">{latestWeatherData ? (latestWeatherData.uvIndex / 10).toFixed(1) : 'No data'}</Typography>
                            </StatRow>
                            <StatRow>
                                <Typography variant="body2" color="textSecondary">Last Updated</Typography>
                                <Typography variant="body1">{latestWeatherData ? new Date(latestWeatherData.timestamp).toLocaleString() : 'No data'}</Typography>
                            </StatRow>
                        </DashboardCard>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default EfficiencyDashboard;